// src/features/bookings/components/BookingDetail/index.jsx
// ✅ Tabbed Design — صفحة تفاصيل الحجز بتبويبات

import React, { useState, useRef } from 'react';
import { Link, useParams }  from 'react-router-dom';
import { useReactToPrint }  from 'react-to-print';

import { useAuth }          from '../../../auth/context/AuthContext';
import useBookingDetail     from '../../hooks/useBookingDetail';

import LoadingSpinner       from '../../../../shared/components/ui/LoadingSpinner';
import EmptyState           from '../../../../shared/components/ui/EmptyState';
import Button               from '../../../../shared/components/ui/Button';
import PrintableBooking     from '../../../../shared/components/print/PrintableBooking';

import BookingInfoTab       from './tabs/BookingInfoTab';
import BookingWorkflowTab   from './tabs/BookingWorkflowTab';
import BookingPaymentsTab   from './tabs/BookingPaymentsTab';
import BookingExpensesTab   from './tabs/BookingExpensesTab';
import BookingTypeDetailsTab from './tabs/BookingTypeDetailsTab';
import PhotoFaceTab         from '../../../photoface/components/PhotoFaceTab';

import {
  BOOKING_TYPE_LABELS,
  BOOKING_STATUS_LABELS,
} from '../../../../shared/utils/formatters';

import styles from './BookingDetail.module.css';

// ─── ثوابت ──────────────────────────────────────────────────
const STATUS_CLS = {
  pending:     styles.statusPending,
  confirmed:   styles.statusConfirmed,
  in_progress: styles.statusInProgress,
  completed:   styles.statusCompleted,
  cancelled:   styles.statusCancelled,
};

const NEXT_ACTION = {
  pending:     { label: 'تأكيد الحجز ←',        next: 'confirmed'   },
  confirmed:   { label: 'بدء التنفيذ ←',         next: 'in_progress' },
  in_progress: { label: 'إكمال الحجز ←',         next: 'completed'   },
  completed:   { label: 'إعادة إلى التنفيذ ←',   next: 'in_progress' },
};

// أيقونة التقويم
const CalIcon = () => (
  <svg width="12" height="12" viewBox="0 0 16 16" fill="none">
    <rect x="1" y="3" width="14" height="12" rx="2"
      stroke="currentColor" strokeWidth="1.2"/>
    <path d="M5 1v4M11 1v4M1 7h14"
      stroke="currentColor" strokeWidth="1.2" strokeLinecap="round"/>
  </svg>
);

// أيقونة الموقع
const PinIcon = () => (
  <svg width="11" height="11" viewBox="0 0 16 16" fill="none">
    <path d="M8 1.5a4.5 4.5 0 0 1 4.5 4.5c0 3-4.5 8.5-4.5 8.5S3.5 9 3.5 6A4.5 4.5 0 0 1 8 1.5z"
      stroke="currentColor" strokeWidth="1.2"/>
    <circle cx="8" cy="6" r="1.5" stroke="currentColor" strokeWidth="1.2"/>
  </svg>
);

// أيقونة الهاتف
const PhoneIcon = () => (
  <svg width="11" height="11" viewBox="0 0 16 16" fill="none">
    <path d="M3 2h3l1.5 3.5-1.75 1.25C6.75 9 7 9.25 9.25 11.25L10.5 9.5 14 11v3c0 1-7 2-11-8C-.5 2 2 1 3 2z"
      stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

// ─── formatNumber داخلي ─────────────────────────────────────
const fmt = (n) => {
  const num = parseFloat(n);
  if (isNaN(num)) return '0';
  return num.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 });
};

// ═══════════════════════════════════════════════════════════
// المكوّن الرئيسي
// ═══════════════════════════════════════════════════════════
const BookingDetail = () => {
  const { id }            = useParams();
  const { hasPermission } = useAuth();
  const printableRef      = useRef(null);

  const [activeTab,  setActiveTab]  = useState('info');
  const [isEditing,  setIsEditing]  = useState(false);
  const [editedData, setEditedData] = useState({});

  const {
    booking,
    settings,
    loading,
    error,
    setError,
    loadData,
    changeStatus,
    removeBooking,
    saveEdit,
  } = useBookingDetail(id);

  const handlePrint = useReactToPrint({
    contentRef: printableRef,
    documentTitle: `booking-${booking?.client?.name}-${booking?.booking_date}`,
  });

  const handleStartEdit  = () => { setEditedData(booking); setIsEditing(true); };
  const handleCancelEdit = () => { setIsEditing(false); setEditedData({}); };

  const handleInputChange = (field, value, nestedField = null) => {
    if (nestedField) {
      setEditedData(prev => ({
        ...prev,
        [nestedField]: { ...(prev[nestedField] ?? {}), [field]: value },
      }));
    } else {
      setEditedData(prev => ({ ...prev, [field]: value }));
    }
  };

  const handleSave = async () => {
    try {
      await saveEdit(editedData);
      setIsEditing(false);
      alert('تم حفظ التعديلات بنجاح!');
    } catch (err) {
      const data = err.response?.data;
      let msg = 'فشل حفظ التعديلات. ';
      if (data && typeof data === 'object') {
        msg += Object.entries(data)
          .map(([f, m]) => `${f}: ${Array.isArray(m) ? m.join(' ') : m}`)
          .join(' | ');
      }
      alert(msg);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm(`هل أنت متأكد من حذف حجز "${booking?.client?.name}"؟`)) return;
    await removeBooking();
  };

  const offerHandlers = {
    handleOfferChange: (index, e) => {
      const { name, value } = e.target;
      const offers = [...(editedData.graduation_details?.offers ?? [])];
      if (offers[index]) {
        offers[index] = { ...offers[index], [name]: value };
        setEditedData(prev => ({
          ...prev,
          graduation_details: { ...(prev.graduation_details ?? {}), offers },
        }));
      }
    },
    addOffer: () => {
      const current = editedData.graduation_details?.offers ?? [];
      setEditedData(prev => ({
        ...prev,
        graduation_details: {
          ...(prev.graduation_details ?? {}),
          offers: [...current, {
            name: `العرض ${current.length + 1}`,
            num_students: '', offer_price: '', printing_cost: '3680.00',
          }],
        },
      }));
    },
    removeOffer: (index) => {
      const current = editedData.graduation_details?.offers ?? [];
      if (current.length <= 1) { alert('يجب أن يحتوي على عرض واحد على الأقل.'); return; }
      setEditedData(prev => ({
        ...prev,
        graduation_details: {
          ...prev.graduation_details,
          offers: current.filter((_, i) => i !== index),
        },
      }));
    },
  };

  // ─── تعريف التبويبات ─────────────────────────────────────
  const TABS = [
    { id: 'info',     label: 'المعلومات',    icon: '📋' },
    { id: 'workflow', label: 'خطة العمل',    icon: '⚙️', count: booking?.deliverables?.length },
    { id: 'payments', label: 'الدفعات',      icon: '💳', count: booking?.payments?.length     },
    { id: 'expenses', label: 'المصاريف',     icon: '💸', count: booking?.expenses?.length     },
    { id: 'typeinfo', label: 'تفاصيل الحجز', icon: '🗂️' },
    { id: 'photoface', label: 'ألبوم الوجوه', icon: '📸', ai: true },
  ];

  // ─── تحميل ──────────────────────────────────────────────
  if (loading) return <LoadingSpinner message="جاري تحميل التفاصيل..." fullPage />;

  if (error || !booking) {
    return (
      <EmptyState
        icon="⚠️"
        title="لا يمكن عرض الحجز"
        message={error ?? 'لم يتم العثور على بيانات الحجز.'}
      />
    );
  }

  const nextAction = NEXT_ACTION[booking.status] ?? null;
  const statusCls  = STATUS_CLS[booking.status] ?? styles.statusPending;

  // الملخص المالي
  const grandTotal = booking.booking_type === 'graduation'
    ? (booking.graduation_details?.grand_total ?? booking.total_price)
    : booking.total_price;

  return (
    <div className={styles.container}>

      {/* مخفي للطباعة */}
      <div style={{ display: 'none' }}>
        <PrintableBooking ref={printableRef} booking={booking} settings={settings} />
      </div>

      {/* ══ HERO ══ */}
      <div className={styles.hero}>

        <div>
          {/* Breadcrumb */}
          <div className={styles.breadcrumb}>
            <Link to="/" className={styles.backLink}>
              <svg width="12" height="12" viewBox="0 0 16 16" fill="none">
                <path d="M10 12L6 8l4-4" stroke="currentColor"
                  strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
              العودة
            </Link>
            <span className={styles.breadSep}>/</span>
            <span className={styles.breadCurrent}>سجل الحجوزات</span>
          </div>

          {/* الاسم والحالة */}
          <div className={styles.heroTitle}>
            <h1 className={styles.clientName}>{booking.client?.name}</h1>
            <span className={`${styles.statusBadge} ${statusCls}`}>
              {BOOKING_STATUS_LABELS[booking.status] ?? booking.status}
            </span>
          </div>

          {/* Meta */}
          <div className={styles.heroMeta}>
            {booking.booking_date && (
              <span className={styles.metaItem}>
                <CalIcon />
                {booking.booking_date}
              </span>
            )}
            <span className={styles.metaDot} />
            <span className={styles.metaItem}>
              {BOOKING_TYPE_LABELS[booking.booking_type] ?? booking.booking_type}
            </span>
            {booking.shooting_location && (
              <>
                <span className={styles.metaDot} />
                <span className={styles.metaItem}>
                  <PinIcon />
                  {booking.shooting_location}
                </span>
              </>
            )}
            {booking.client?.phone && (
              <>
                <span className={styles.metaDot} />
                <span className={styles.metaItem}>
                  <PhoneIcon />
                  {booking.client.phone}
                </span>
              </>
            )}
          </div>
        </div>

        {/* الأزرار */}
        <div className={styles.heroActions}>
          <div className={styles.actionsRow}>
            {/* طباعة */}
            <Button variant="secondary" size="sm" onClick={handlePrint}>
              طباعة PDF
            </Button>

            {/* الإجراء الرئيسي */}
            {nextAction && hasPermission('change_booking_status') && (
              <Button variant="primary" size="sm"
                onClick={() => changeStatus(nextAction.next)}>
                {nextAction.label}
              </Button>
            )}

            {/* تعديل / حفظ / إلغاء */}
            {hasPermission('change_booking') && !isEditing && (
              <Button variant="info" size="sm" onClick={handleStartEdit}>تعديل</Button>
            )}
            {isEditing && (
              <>
                <Button variant="primary" size="sm" onClick={handleSave}>حفظ</Button>
                <Button variant="secondary" size="sm" onClick={handleCancelEdit}>إلغاء</Button>
              </>
            )}

            {/* حذف */}
            {hasPermission('delete_booking') && !isEditing && (
              <Button variant="danger" size="sm" onClick={handleDelete}>حذف</Button>
            )}
          </div>
        </div>

      </div>

      {/* ══ FINANCIAL STRIP ══ */}
      <div className={styles.finStrip}>
        <div className={styles.finCell}>
          <span className={styles.finLabel}>الإجمالي الكلي</span>
          <span className={`${styles.finVal} ${styles.finValBlue}`}>
            {fmt(grandTotal)} <small style={{ fontSize: '11px', fontWeight: 400 }}>د.ع</small>
          </span>
        </div>
        <div className={styles.finCell}>
          <span className={styles.finLabel}>إجمالي المدفوع</span>
          <span className={`${styles.finVal} ${styles.finValGreen}`}>
            {fmt(booking.total_paid)} <small style={{ fontSize: '11px', fontWeight: 400 }}>د.ع</small>
          </span>
        </div>
        <div className={styles.finCell}>
          <span className={styles.finLabel}>المبلغ المتبقي</span>
          <span className={`${styles.finVal} ${parseFloat(booking.remaining_amount) > 0 ? styles.finValRed : ''}`}>
            {fmt(booking.remaining_amount)} <small style={{ fontSize: '11px', fontWeight: 400 }}>د.ع</small>
          </span>
        </div>
        <div className={styles.finCell}>
          <span className={styles.finLabel}>إجمالي المصاريف</span>
          <span className={styles.finVal}>
            {fmt(booking.total_expenses ?? booking.expenses?.reduce((s,e) => s + parseFloat(e.amount||0), 0) ?? 0)} <small style={{ fontSize: '11px', fontWeight: 400 }}>د.ع</small>
          </span>
        </div>
      </div>

      {/* رسالة خطأ */}
      {error && <p className={styles.errorMsg}>{error}</p>}

      {/* ══ TABS ══ */}
      <div className={styles.tabsWrapper}>

        {/* شريط التبويبات */}
        <div className={styles.tabsBar}>
          {TABS.map(tab => {
            const active = activeTab === tab.id;
            const aiStyle = tab.ai ? {
              background: active
                ? 'linear-gradient(135deg,#7c3aed,#2563eb)'
                : 'linear-gradient(135deg,rgba(124,58,237,0.12),rgba(37,99,235,0.12))',
              color: active ? '#fff' : '#6d28d9',
              border: '1px solid rgba(124,58,237,0.35)',
              borderRadius: 10,
              fontWeight: 700,
            } : undefined;
            return (
              <button
                key={tab.id}
                type="button"
                className={`${styles.tab} ${active ? styles.tabActive : ''}`}
                onClick={() => setActiveTab(tab.id)}
                style={aiStyle}
              >
                <span className={styles.tabIcon}>{tab.icon}</span>
                {tab.label}
                {tab.ai && (
                  <span style={{
                    marginInlineStart: 6,
                    fontSize: 10,
                    fontWeight: 800,
                    padding: '2px 7px',
                    borderRadius: 999,
                    background: active ? 'rgba(255,255,255,0.28)' : 'linear-gradient(135deg,#7c3aed,#2563eb)',
                    color: '#fff',
                    letterSpacing: '0.3px',
                    whiteSpace: 'nowrap',
                  }}>
                    ✨ AI
                  </span>
                )}
                {tab.count > 0 && (
                  <span className={styles.tabCount}>{tab.count}</span>
                )}
              </button>
            );
          })}
        </div>

        {/* محتوى التبويبات */}
        <div className={styles.tabBody}>

          {/* ── المعلومات ── */}
          {activeTab === 'info' && (
            <BookingInfoTab
              booking={booking}
              editedData={editedData}
              isEditing={isEditing}
              onInputChange={handleInputChange}
              styles={styles}
            />
          )}

          {/* ── خطة العمل ── */}
          {activeTab === 'workflow' && (
            <BookingWorkflowTab
              booking={booking}
              onDataChange={loadData}
            />
          )}

          {/* ── الدفعات ── */}
          {activeTab === 'payments' && (
            <BookingPaymentsTab
              booking={booking}
              onDataRefresh={loadData}
              canAdd={hasPermission('add_payment')}
              canDelete={hasPermission('delete_payment')}
              styles={styles}
            />
          )}

          {/* ── المصاريف ── */}
          {activeTab === 'expenses' && (
            <BookingExpensesTab
              booking={booking}
              onDataRefresh={loadData}
              canAdd={hasPermission('add_expense')}
              canDelete={hasPermission('delete_expense')}
              styles={styles}
            />
          )}

          {/* ── تفاصيل الحجز ── */}
          {activeTab === 'typeinfo' && (
            <BookingTypeDetailsTab
              booking={booking}
              editedData={editedData}
              isEditing={isEditing}
              onInputChange={handleInputChange}
              offerHandlers={offerHandlers}
              styles={styles}
            />
          )}

          {/* ── ألبوم الوجوه (PhotoFace) ── */}
          {activeTab === 'photoface' && (
            <PhotoFaceTab booking={booking} />
          )}

        </div>
      </div>

    </div>
  );
};

export default BookingDetail;